/**
 * UI Store
 * Manages UI state like sidebar, theme, and other interface-related state
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { useMemo } from "react";

// UI state structure
const initialUIState = {
  sidebarOpen: true,
  theme: "dark",
};

const applyThemeToDocument = (theme) => {
  if (typeof document === "undefined") return;

  const isDark = theme === "dark";
  document.documentElement.classList.add("ordering-theme");
  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
};

/**
 * UI Store
 * Handles all UI-related state and actions
 */
export const useUIStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialUIState,

        // Sidebar actions
        toggleSidebar: () => {
          set((state) => ({ sidebarOpen: !state.sidebarOpen }));
        },

        setSidebarOpen: (open) => {
          set({ sidebarOpen: open });
        },

        // Theme actions
        setTheme: (theme) => {
          set({ theme });
          applyThemeToDocument(theme);
        },

        // Initialize theme on store creation
        initializeTheme: () => {
          applyThemeToDocument(get().theme);
        },
      }),
      {
        name: "ui-store",
        // Only persist theme, not sidebar state
        partialize: (state) => ({
          theme: state.theme,
        }),
      },
    ),
    {
      name: "ui-store",
    },
  ),
);

// Individual selectors to avoid object recreation
export const useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);
export const useToggleSidebar = () =>
  useUIStore((state) => state.toggleSidebar);
export const useSetSidebarOpen = () =>
  useUIStore((state) => state.setSidebarOpen);

export const useTheme = () => useUIStore((state) => state.theme);
export const useSetTheme = () => useUIStore((state) => state.setTheme);
export const useInitializeTheme = () =>
  useUIStore((state) => state.initializeTheme);

// Combined selectors with proper memoization using useMemo
export const useSidebarState = () => {
  const sidebarOpen = useSidebarOpen();
  const toggleSidebar = useToggleSidebar();
  const setSidebarOpen = useSetSidebarOpen();

  return useMemo(
    () => ({
      sidebarOpen,
      toggleSidebar,
      setSidebarOpen,
    }),
    [sidebarOpen, toggleSidebar, setSidebarOpen],
  );
};

export const useThemeState = () => {
  const theme = useTheme();
  const setTheme = useSetTheme();

  return useMemo(
    () => ({
      theme,
      setTheme,
    }),
    [theme, setTheme],
  );
};
